(() => {
  'use strict';

  if (customElements.get('fd-predictive-search')) return;

  const escapeHtml = (value = '') => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  class FDPredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('[data-fd-predictive-input]');
      this.form = this.querySelector('[data-fd-predictive-form]');
      this.panel = this.querySelector('[data-fd-predictive-panel]');
      this.results = this.querySelector('[data-fd-predictive-results]');
      this.status = this.querySelector('[data-fd-predictive-status]');
      this.clearButton = this.querySelector('[data-fd-predictive-clear]');
      this.cache = new Map();
      this.abortController = null;
      this.debounceTimer = 0;
      this.activeIndex = -1;

      this.onInput = this.onInput.bind(this);
      this.onFocus = this.onFocus.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
    }

    connectedCallback() {
      if (!this.input || !this.panel || !this.results) return;

      this.ensureSearchOptions();
      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('focus', this.onFocus);
      this.input.addEventListener('keydown', this.onKeydown);
      this.form?.addEventListener('reset', () => {
        window.setTimeout(() => {
          this.close();
          this.updateClearButton();
          this.input?.focus();
        });
      });
      this.form?.addEventListener('submit', () => this.close());
      document.addEventListener('click', this.onDocumentClick);
      this.updateClearButton();
    }

    ensureSearchOptions() {
      if (!this.form) return;

      const options = {
        type: 'product',
        'options[prefix]': 'last',
        'options[unavailable_products]': 'hide'
      };

      Object.entries(options).forEach(([name, value]) => {
        const fieldExists = [...this.form.elements].some((field) => field.name === name);
        if (fieldExists) return;

        const field = document.createElement('input');
        field.type = 'hidden';
        field.name = name;
        field.value = value;
        this.form.append(field);
      });
    }

    disconnectedCallback() {
      this.input?.removeEventListener('input', this.onInput);
      this.input?.removeEventListener('focus', this.onFocus);
      this.input?.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onDocumentClick);
      window.clearTimeout(this.debounceTimer);
      this.abortController?.abort();
    }

    onInput() {
      const query = this.input.value.trim();
      this.updateClearButton();
      window.clearTimeout(this.debounceTimer);

      if (query.length < 2) {
        this.abortController?.abort();
        this.close();
        return;
      }

      this.debounceTimer = window.setTimeout(() => this.search(query), 220);
    }

    onFocus() {
      const query = this.input.value.trim();
      if (query.length < 2) return;
      if (this.cache.has(query.toLocaleLowerCase(this.locale))) {
        this.render(this.cache.get(query.toLocaleLowerCase(this.locale)), query);
      } else {
        this.search(query);
      }
    }

    onDocumentClick(event) {
      if (!this.contains(event.target)) this.close();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }

      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      const options = [...this.panel.querySelectorAll('[data-fd-predictive-option]')];
      if (!options.length || this.panel.hidden) return;

      event.preventDefault();
      this.activeIndex += event.key === 'ArrowDown' ? 1 : -1;
      if (this.activeIndex >= options.length) this.activeIndex = 0;
      if (this.activeIndex < 0) this.activeIndex = options.length - 1;
      options[this.activeIndex].focus();
    }

    get locale() {
      return this.dataset.locale || document.documentElement.lang || 'de-DE';
    }

    get currency() {
      return this.dataset.currency || 'EUR';
    }

    async search(query) {
      const cacheKey = query.toLocaleLowerCase(this.locale);
      if (this.cache.has(cacheKey)) {
        this.render(this.cache.get(cacheKey), query);
        return;
      }

      this.abortController?.abort();
      this.abortController = new AbortController();
      this.setLoading(true);
      this.open();

      try {
        const endpoint = new URL(this.dataset.searchUrl || '/search/suggest.json', window.location.origin);
        endpoint.searchParams.set('q', query);
        endpoint.searchParams.set('resources[type]', 'query,product,collection,page');
        endpoint.searchParams.set('resources[limit]', '6');
        endpoint.searchParams.set('resources[limit_scope]', 'each');
        endpoint.searchParams.set('resources[options][unavailable_products]', 'hide');
        // Search the descriptive copy as well. Fashion Drinks collection terms such as
        // "Gravelbikes" often live in the product copy or tags instead of the title.
        endpoint.searchParams.set('resources[options][fields]', 'title,body,product_type,variants.title,vendor,tag');

        const response = await fetch(endpoint.toString(), {
          signal: this.abortController.signal,
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error(`Predictive search returned ${response.status}`);

        const data = await response.json();
        let resultSet = data?.resources?.results || data?.resources || {};
        resultSet = await this.addMatchedCollectionProducts(
          resultSet,
          query,
          this.abortController.signal
        );
        this.cache.set(cacheKey, resultSet);

        if (query === this.input.value.trim()) this.render(resultSet, query);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.renderError(query);
      } finally {
        this.setLoading(false);
      }
    }

    normalizeSearchText(value = '') {
      return String(value)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLocaleLowerCase(this.locale)
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
    }

    isStrongCollectionMatch(collection, query) {
      const normalizedQuery = this.normalizeSearchText(query).replace(/\s+/g, '');
      const normalizedTitle = this.normalizeSearchText(collection?.title).replace(/\s+/g, '');

      return normalizedQuery.length >= 3 && normalizedTitle.startsWith(normalizedQuery);
    }

    async addMatchedCollectionProducts(resultSet, query, signal) {
      const collections = Array.isArray(resultSet.collections) ? resultSet.collections : [];
      const matchedCollection = collections.find((collection) =>
        this.isStrongCollectionMatch(collection, query)
      );

      if (!matchedCollection?.url) return resultSet;

      try {
        const collectionUrl = new URL(matchedCollection.url, window.location.origin);
        if (collectionUrl.origin !== window.location.origin) return resultSet;

        collectionUrl.search = '';
        collectionUrl.hash = '';
        collectionUrl.pathname = `${collectionUrl.pathname.replace(/\/$/, '')}/products.json`;
        collectionUrl.searchParams.set('limit', '6');

        const response = await fetch(collectionUrl.toString(), {
          signal,
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) return resultSet;

        const data = await response.json();
        const products = Array.isArray(data?.products)
          ? data.products
            .filter((product) => product.variants?.some((variant) => variant.available))
            .slice(0, 6)
            .map((product) => this.normalizeCollectionProduct(product))
          : [];

        if (!products.length) return resultSet;

        return {
          ...resultSet,
          products,
          fdCollectionTitle: matchedCollection.title
        };
      } catch (error) {
        if (error.name === 'AbortError') throw error;
        return resultSet;
      }
    }

    normalizeCollectionProduct(product) {
      const availableVariant = product.variants?.find((variant) => variant.available)
        || product.variants?.[0]
        || null;
      const featuredImage = product.images?.[0]
        || availableVariant?.featured_image
        || null;
      const root = window.Shopify?.routes?.root || '/';
      const url = new URL(`${root}products/${product.handle}`, window.location.origin);

      return {
        ...product,
        url: `${url.pathname}${url.search}`,
        price: availableVariant?.price ?? product.price,
        featured_image: featuredImage
      };
    }

    render(resultSet, query) {
      const products = Array.isArray(resultSet.products) ? resultSet.products.slice(0, 6) : [];
      const collections = Array.isArray(resultSet.collections) ? resultSet.collections.slice(0, 4) : [];
      const queries = Array.isArray(resultSet.queries) ? resultSet.queries.slice(0, 4) : [];
      const pages = Array.isArray(resultSet.pages) ? resultSet.pages.slice(0, 3) : [];
      const count = products.length + collections.length + queries.length + pages.length;

      if (!count) {
        this.results.innerHTML = `
          <div class="fd-predictive__empty">
            <strong>Keine direkten Treffer</strong>
            <span>Suche trotzdem nach „${escapeHtml(query)}“.</span>
          </div>
          ${this.renderFullSearchLink(query)}
        `;
        this.setStatus(`Keine Vorschläge für ${query}`);
        this.open();
        return;
      }

      const queryMarkup = queries.length ? `
        <section class="fd-predictive__group fd-predictive__group--queries">
          <h3>Suchvorschläge</h3>
          <div class="fd-predictive__chips">
            ${queries.map((item) => `
              <a href="${escapeHtml(item.url || this.fullSearchUrl(item.text || query))}" data-fd-predictive-option>
                ${escapeHtml(item.text || query)}
              </a>
            `).join('')}
          </div>
        </section>
      ` : '';

      const productHeading = resultSet.fdCollectionTitle
        ? `Produkte aus ${escapeHtml(resultSet.fdCollectionTitle)}`
        : 'Produkte';
      const productMarkup = products.length ? `
        <section class="fd-predictive__group fd-predictive__group--products">
          <h3>${productHeading}</h3>
          <div class="fd-predictive__products">
            ${products.map((product) => this.renderProduct(product)).join('')}
          </div>
        </section>
      ` : '';

      const collectionMarkup = collections.length ? `
        <section class="fd-predictive__group fd-predictive__group--collections">
          <h3>Designs &amp; Kollektionen</h3>
          <div class="fd-predictive__links">
            ${collections.map((collection) => `
              <a href="${escapeHtml(collection.url)}" data-fd-predictive-option>
                <span>${escapeHtml(collection.title)}</span><span aria-hidden="true">→</span>
              </a>
            `).join('')}
          </div>
        </section>
      ` : '';

      const pageMarkup = pages.length ? `
        <section class="fd-predictive__group fd-predictive__group--pages">
          <h3>Weitere Treffer</h3>
          <div class="fd-predictive__links">
            ${pages.map((page) => `
              <a href="${escapeHtml(page.url)}" data-fd-predictive-option>
                <span>${escapeHtml(page.title)}</span><span aria-hidden="true">→</span>
              </a>
            `).join('')}
          </div>
        </section>
      ` : '';

      this.results.innerHTML = `
        ${queryMarkup}
        ${productMarkup}
        <div class="fd-predictive__secondary">
          ${collectionMarkup}
          ${pageMarkup}
        </div>
        ${this.renderFullSearchLink(query)}
      `;
      this.setStatus(`${count} Vorschläge für ${query}`);
      this.activeIndex = -1;
      this.open();
    }

    renderProduct(product) {
      const featuredImage = product.featured_image || product.image || null;
      const imageUrl = typeof featuredImage === 'string'
        ? featuredImage
        : featuredImage?.url || featuredImage?.src || '';
      const imageAlt = typeof featuredImage === 'object' ? featuredImage?.alt || product.title : product.title;
      const price = product.price ?? product.price_min;

      return `
        <a class="fd-predictive__product" href="${escapeHtml(product.url)}" data-fd-predictive-option>
          <span class="fd-predictive__product-image">
            ${imageUrl
              ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(imageAlt)}" loading="lazy" width="240" height="240">`
              : '<span class="fd-predictive__image-placeholder" aria-hidden="true"></span>'}
          </span>
          <span class="fd-predictive__product-copy">
            <strong>${escapeHtml(product.title)}</strong>
            ${price !== undefined && price !== null ? `<small>${escapeHtml(this.formatMoney(price))}</small>` : ''}
          </span>
        </a>
      `;
    }

    renderFullSearchLink(query) {
      return `
        <a class="fd-predictive__all" href="${escapeHtml(this.fullSearchUrl(query))}" data-fd-predictive-option>
          <span>Alle Ergebnisse für „${escapeHtml(query)}“</span>
          <span aria-hidden="true">→</span>
        </a>
      `;
    }

    renderError(query) {
      this.results.innerHTML = `
        <div class="fd-predictive__empty">
          <strong>Vorschläge konnten gerade nicht geladen werden.</strong>
          <span>Die normale Suche funktioniert weiterhin.</span>
        </div>
        ${this.renderFullSearchLink(query)}
      `;
      this.setStatus('Suchvorschläge konnten nicht geladen werden');
      this.open();
    }

    fullSearchUrl(query) {
      const url = new URL(this.dataset.fullSearchUrl || '/search', window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      url.searchParams.set('options[unavailable_products]', 'hide');
      return `${url.pathname}${url.search}`;
    }

    formatMoney(value) {
      const number = Number.parseFloat(value);
      if (!Number.isFinite(number)) return String(value);
      return new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: 2
      }).format(number);
    }

    setLoading(isLoading) {
      this.toggleAttribute('aria-busy', isLoading);
      if (isLoading) {
        this.results.innerHTML = '<div class="fd-predictive__loading"><span></span><span></span><span></span></div>';
        this.setStatus('Suchvorschläge werden geladen');
      }
    }

    setStatus(message) {
      if (this.status) this.status.textContent = message;
    }

    updateClearButton() {
      if (this.clearButton) this.clearButton.hidden = !this.input.value;
    }

    open() {
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.activeIndex = -1;
    }
  }

  customElements.define('fd-predictive-search', FDPredictiveSearch);
})();
